import React, { useState, useEffect, useCallback } from 'react';
import { useAuth } from '../../contexts/AuthContext';
import axios from 'axios';
import { API_URL } from '../../config/apiConfig';
import TrackedChangesView from '../TrackedChangesView/TrackedChangesView';
import '../../styles/ampDescriptionDisplay.css';
import './ModerationScreen.css';

const api = axios.create({ baseURL: API_URL });

const WIKI_SECTION_LABELS = {
  description: 'Description',
  maintenance: 'Maintenance & Tips',
  variants: 'Variants & Revisions',
  notable_users: 'Notable Users',
  technical_specs: 'Technical Specs',
  history: 'History',
  tone_character: 'Tone & Character',
  market_collectibility: 'Market & Collectibility',
};

const CONTENT_TYPE_LABELS = {
  gallery_photo: 'Gallery Photo',
  hero_image: 'Hero Image',
  bg_image: 'Background Image',
  collection_photo: 'Collection Photo',
  amplifier_artifact: 'Amplifier Artifact',
  profile_photo: 'Profile Photo',
  discussion_post: 'Discussion Post',
  discussion_message: 'Discussion Message'
};

const STATUS_COLORS = {
  pending: '#f59e0b',
  approved: '#10b981',
  rejected: '#ef4444',
  dismissed: '#6b7280'
};

// Helper: <img> tags can't send Authorization headers, so we fetch
// the image as a blob with the token and use an object URL.
const AuthImage = ({ src, alt, className, token, onError }) => {
  const [objectUrl, setObjectUrl] = React.useState(null);
  const [failed, setFailed] = React.useState(false);

  React.useEffect(() => {
    let revoke;
    if (!src || !token) return;
    (async () => {
      try {
        const res = await fetch(src, { headers: { Authorization: `Bearer ${token}` } });
        if (!res.ok) throw new Error(res.status);
        const blob = await res.blob();
        const url = URL.createObjectURL(blob);
        revoke = url;
        setObjectUrl(url);
      } catch {
        setFailed(true);
        if (onError) onError();
      }
    })();
    return () => { if (revoke) URL.revokeObjectURL(revoke); };
  }, [src, token, onError]);

  if (failed) return null;
  if (!objectUrl) return <div className={className} style={{ background: '#f3f4f6' }} />;
  return <img src={objectUrl} alt={alt} className={className} />;
};

const ModerationScreen = () => {
  const { token, isAdmin, isSiteModerator } = useAuth();

  // Filter state
  const [statusFilter, setStatusFilter] = useState('pending');
  const [contentTypeFilter, setContentTypeFilter] = useState('');
  const [page, setPage] = useState(1);
  const limit = 20;

  // Data state
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);

  // Review state
  const [selectedItem, setSelectedItem] = useState(null);
  const [reviewAction, setReviewAction] = useState('');
  const [reviewNotes, setReviewNotes] = useState('');
  const [reviewing, setReviewing] = useState(false);

  // Board moderator management state
  const [showBoardMods, setShowBoardMods] = useState(false);
  const [boardModerators, setBoardModerators] = useState([]);
  const [loadingBoardMods, setLoadingBoardMods] = useState(false);

  // Tab state: 'content', 'wiki', or 'parts'
  const [activeTab, setActiveTab] = useState('content');

  // Wiki edits state
  const [wikiRevisions, setWikiRevisions] = useState([]);
  const [wikiTotal, setWikiTotal] = useState(0);
  const [wikiPage, setWikiPage] = useState(1);
  const [wikiLoading, setWikiLoading] = useState(false);
  const [selectedWikiRevision, setSelectedWikiRevision] = useState(null);
  const [wikiReviewNote, setWikiReviewNote] = useState('');
  const [wikiReviewing, setWikiReviewing] = useState(false);
  const [wikiEditorContent, setWikiEditorContent] = useState(''); // moderator's edited content
  const [wikiContentModified, setWikiContentModified] = useState(false); // track if moderator edited

  // Parts list moderation state
  const [partsSubmissions, setPartsSubmissions] = useState([]);
  const [partsTotal, setPartsTotal] = useState(0);
  const [partsLoading, setPartsLoading] = useState(false);
  const [selectedPartsSubmission, setSelectedPartsSubmission] = useState(null);
  const [partsSubmissionItems, setPartsSubmissionItems] = useState([]);
  const [partsReviewNote, setPartsReviewNote] = useState('');
  const [partsReviewing, setPartsReviewing] = useState(false);
  const [editingPartItem, setEditingPartItem] = useState(null); // item being inline-edited
  const [editingPartValues, setEditingPartValues] = useState({});
  const [addingPartItem, setAddingPartItem] = useState(false);
  const [newPartItem, setNewPartItem] = useState({ section: '', part_name: '', value_spec: '', quantity: 1, notes: '' });

  // Set up auth header
  useEffect(() => {
    if (token) {
      api.defaults.headers.common['Authorization'] = `Bearer ${token}`;
    }
  }, [token]);

  // Fetch moderation queue
  const fetchQueue = useCallback(async () => {
    setLoading(true);
    try {
      const params = { page, limit };
      if (statusFilter && statusFilter !== 'all') params.status = statusFilter;
      if (contentTypeFilter) params.content_type = contentTypeFilter;

      const response = await api.get('/moderation/queue', { params });
      setItems(response.data.items || []);
      setTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch moderation queue:', err);
    } finally {
      setLoading(false);
    }
  }, [page, statusFilter, contentTypeFilter]);

  // Fetch stats
  const fetchStats = useCallback(async () => {
    try {
      const response = await api.get('/moderation/stats');
      setStats(response.data);
    } catch (err) {
      console.error('Failed to fetch moderation stats:', err);
    }
  }, []);

  useEffect(() => { fetchQueue(); }, [fetchQueue]);
  useEffect(() => { fetchStats(); }, [fetchStats]);

  // Fetch wiki count on mount so the tab badge shows immediately
  useEffect(() => {
    (async () => {
      try {
        const response = await api.get('/wiki/pending', { params: { page: 1 } });
        setWikiTotal(response.data.total || 0);
      } catch { /* silent */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Fetch parts list moderation count on mount so the tab badge shows immediately
  useEffect(() => {
    (async () => {
      try {
        const response = await api.get('/parts-list-moderation/queue');
        setPartsTotal((response.data || []).length);
      } catch { /* silent */ }
    })();
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Review a queue item
  const handleReview = async () => {
    if (!selectedItem || !reviewAction) return;
    setReviewing(true);
    try {
      await api.put(`/moderation/queue/${selectedItem.moderation_id}/review`, {
        action: reviewAction,
        notes: reviewNotes || null
      });
      setSelectedItem(null);
      setReviewAction('');
      setReviewNotes('');
      fetchQueue();
      fetchStats();
    } catch (err) {
      console.error('Failed to review item:', err);
      alert('Failed to review item: ' + (err.response?.data?.error || err.message));
    } finally {
      setReviewing(false);
    }
  };

  // Fetch board moderators
  const fetchBoardModerators = useCallback(async () => {
    if (!isAdmin && !isSiteModerator) return;
    setLoadingBoardMods(true);
    try {
      const response = await api.get('/moderation/board-moderators');
      setBoardModerators(response.data || []);
    } catch (err) {
      console.error('Failed to fetch board moderators:', err);
    } finally {
      setLoadingBoardMods(false);
    }
  }, [isAdmin, isSiteModerator]);

  useEffect(() => {
    if (showBoardMods) fetchBoardModerators();
  }, [showBoardMods, fetchBoardModerators]);

  // Remove board moderator
  const handleRemoveBoardMod = async (id) => {
    if (!window.confirm('Remove this board moderator assignment?')) return;
    try {
      await api.delete(`/moderation/board-moderators/${id}`);
      fetchBoardModerators();
    } catch (err) {
      alert('Failed to remove: ' + (err.response?.data?.error || err.message));
    }
  };

  // Fetch pending wiki revisions
  const fetchWikiRevisions = useCallback(async () => {
    setWikiLoading(true);
    try {
      const response = await api.get('/wiki/pending', { params: { page: wikiPage } });
      setWikiRevisions(response.data.revisions || []);
      setWikiTotal(response.data.total || 0);
    } catch (err) {
      console.error('Failed to fetch wiki revisions:', err);
    } finally {
      setWikiLoading(false);
    }
  }, [wikiPage]);

  useEffect(() => {
    if (activeTab === 'wiki') fetchWikiRevisions();
  }, [activeTab, fetchWikiRevisions]);

  // Review a wiki revision
  const handleWikiReview = async (revisionId, action) => {
    // Require a note when requesting changes
    if (action === 'request_changes' && !wikiReviewNote.trim()) {
      alert('Please describe what changes you would like the contributor to make.');
      return;
    }
    setWikiReviewing(true);
    try {
      const body = {
        action,
        review_note: wikiReviewNote || undefined,
        was_modified: wikiContentModified
      };
      if (action === 'request_changes' && wikiContentModified && wikiEditorContent.trim()) {
        body.suggested_content = `__RAW_HTML__${wikiEditorContent}`;
      }
      await api.post(`/wiki/revisions/${revisionId}/review`, body);
      setSelectedWikiRevision(null);
      setWikiReviewNote('');
      setWikiEditorContent('');
      setWikiContentModified(false);
      fetchWikiRevisions();
    } catch (err) {
      console.error('Failed to review wiki edit:', err);
      alert('Failed to review: ' + (err.response?.data?.error || err.message));
    } finally {
      setWikiReviewing(false);
    }
  };

  // Fetch parts list moderation queue
  const fetchPartsQueue = useCallback(async () => {
    setPartsLoading(true);
    try {
      const response = await api.get('/parts-list-moderation/queue');
      setPartsSubmissions(response.data || []);
      setPartsTotal((response.data || []).length);
    } catch (err) {
      console.error('Failed to fetch parts moderation queue:', err);
    } finally {
      setPartsLoading(false);
    }
  }, []);

  useEffect(() => {
    if (activeTab === 'parts') fetchPartsQueue();
  }, [activeTab, fetchPartsQueue]);

  // Load items for a selected parts submission
  const loadSubmissionItems = async (submission) => {
    // Items are now included in the queue response, but we can refresh from there
    try {
      const response = await api.get('/parts-list-moderation/queue');
      const allSubs = response.data || [];
      setPartsSubmissions(allSubs);
      setPartsTotal(allSubs.length);
      const found = allSubs.find(s => s.submission_id === submission.submission_id);
      if (found) {
        setSelectedPartsSubmission(found);
        setPartsSubmissionItems(found.items || []);
      } else {
        // Submission was approved/rejected since we last loaded
        setSelectedPartsSubmission(null);
        setPartsSubmissionItems([]);
      }
    } catch (err) {
      console.error('Failed to refresh submission items:', err);
    }
  };

  // Approve or reject a parts submission
  const handlePartsReview = async (submissionId, action) => {
    if (action === 'reject' && !partsReviewNote.trim()) {
      alert('Please provide a reason for rejecting the submission.');
      return;
    }
    setPartsReviewing(true);
    try {
      await api.post(`/parts-list-submissions/${submissionId}/${action}`, {
        review_note: partsReviewNote || undefined
      });
      setSelectedPartsSubmission(null);
      setPartsSubmissionItems([]);
      setPartsReviewNote('');
      fetchPartsQueue();
    } catch (err) {
      console.error(`Failed to ${action} parts submission:`, err);
      alert(`Failed to ${action}: ` + (err.response?.data?.error || err.message));
    } finally {
      setPartsReviewing(false);
    }
  };

  // Inline edit a submission item
  const handleSavePartItemEdit = async (submissionId, itemId) => {
    try {
      await api.put(`/parts-list-submissions/${submissionId}/items/${itemId}`, editingPartValues);
      setEditingPartItem(null);
      setEditingPartValues({});
      // Reload items
      await loadSubmissionItems(selectedPartsSubmission);
    } catch (err) {
      alert('Failed to update item: ' + (err.response?.data?.error || err.message));
    }
  };

  // Delete a submission item
  const handleDeletePartItem = async (submissionId, itemId) => {
    if (!window.confirm('Remove this item from the submission?')) return;
    try {
      await api.delete(`/parts-list-submissions/${submissionId}/items/${itemId}`);
      await loadSubmissionItems(selectedPartsSubmission);
    } catch (err) {
      alert('Failed to delete item: ' + (err.response?.data?.error || err.message));
    }
  };

  // Add a new item to the submission
  const handleAddPartItem = async (submissionId) => {
    if (!newPartItem.section.trim() || !newPartItem.part_name.trim()) {
      alert('Section and Part Name are required.');
      return;
    }
    try {
      await api.post(`/parts-list-submissions/${submissionId}/items`, {
        ...newPartItem,
        edit_type: 'add'
      });
      setAddingPartItem(false);
      setNewPartItem({ section: '', part_name: '', value_spec: '', quantity: 1, notes: '' });
      await loadSubmissionItems(selectedPartsSubmission);
    } catch (err) {
      alert('Failed to add item: ' + (err.response?.data?.error || err.message));
    }
  };

  const totalPages = Math.ceil(total / limit);

  return (
    <div className="moderation-screen">
      <div className="moderation-header">
        <h1>Content Moderation</h1>
        <p className="moderation-subtitle">
          Review flagged content and manage community reports
        </p>
      </div>

      {/* Stats Cards */}
      {stats && (
        <div className="moderation-stats">
          <div className="stat-card stat-pending" onClick={() => { setActiveTab('content'); setStatusFilter('pending'); setPage(1); }}>
            <div className="stat-value">{stats.pending}</div>
            <div className="stat-label">Pending</div>
          </div>
          <div className="stat-card stat-approved" onClick={() => { setActiveTab('content'); setStatusFilter('approved'); setPage(1); }}>
            <div className="stat-value">{stats.approved}</div>
            <div className="stat-label">Approved</div>
          </div>
          <div className="stat-card stat-rejected" onClick={() => { setActiveTab('content'); setStatusFilter('rejected'); setPage(1); }}>
            <div className="stat-value">{stats.rejected}</div>
            <div className="stat-label">Rejected</div>
          </div>
          <div className="stat-card stat-dismissed" onClick={() => { setActiveTab('content'); setStatusFilter('dismissed'); setPage(1); }}>
            <div className="stat-value">{stats.dismissed}</div>
            <div className="stat-label">Dismissed</div>
          </div>
        </div>
      )}

      {/* Tabs */}
      <div className="moderation-tabs">
        <button
          className={`mod-tab ${activeTab === 'content' ? 'active' : ''}`}
          onClick={() => setActiveTab('content')}
        >
          🖼️ Content Queue
        </button>
        <button
          className={`mod-tab ${activeTab === 'wiki' ? 'active' : ''}`}
          onClick={() => setActiveTab('wiki')}
        >
          ✏️ Wiki Edits
          {wikiTotal > 0 && activeTab !== 'wiki' && (
            <span className="wiki-badge">{wikiTotal}</span>
          )}
        </button>
        <button
          className={`mod-tab ${activeTab === 'parts' ? 'active' : ''}`}
          onClick={() => setActiveTab('parts')}
        >
          🔧 Parts List
          {partsTotal > 0 && activeTab !== 'parts' && (
            <span className="wiki-badge">{partsTotal}</span>
          )}
        </button>
      </div>

      {/* ==================== CONTENT QUEUE TAB ==================== */}
      {activeTab === 'content' && (
        <>
      {/* Toolbar */}
      <div className="moderation-toolbar">
        <div className="toolbar-filters">
          <select
            value={statusFilter}
            onChange={(e) => { setStatusFilter(e.target.value); setPage(1); }}
            className="filter-select"
          >
            <option value="all">All Status</option>
            <option value="pending">Pending</option>
            <option value="approved">Approved</option>
            <option value="rejected">Rejected</option>
            <option value="dismissed">Dismissed</option>
          </select>

          <select
            value={contentTypeFilter}
            onChange={(e) => { setContentTypeFilter(e.target.value); setPage(1); }}
            className="filter-select"
          >
            <option value="">All Content Types</option>
            {Object.entries(CONTENT_TYPE_LABELS).map(([key, label]) => (
              <option key={key} value={key}>{label}</option>
            ))}
          </select>
        </div>

        {(isAdmin || isSiteModerator) && (
          <button
            className="btn-board-mods"
            onClick={() => setShowBoardMods(!showBoardMods)}
          >
            {showBoardMods ? 'Hide Board Mods' : 'Manage Board Mods'}
          </button>
        )}
      </div>

      {/* Board Moderator Management Panel */}
      {showBoardMods && (isAdmin || isSiteModerator) && (
        <div className="board-mods-panel">
          <h3>Board Moderator Assignments</h3>
          {loadingBoardMods ? (
            <p>Loading...</p>
          ) : boardModerators.length === 0 ? (
            <p className="no-data">No board moderators assigned yet.</p>
          ) : (
            <table className="board-mods-table">
              <thead>
                <tr>
                  <th>User</th>
                  <th>Discussion Board</th>
                  <th>Assigned By</th>
                  <th>Assigned</th>
                  <th>Status</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {boardModerators.map(bm => (
                  <tr key={bm.board_moderator_id}>
                    <td>{bm.username} ({bm.email})</td>
                    <td>{bm.group_name}</td>
                    <td>{bm.assigned_by_username || '—'}</td>
                    <td>{new Date(bm.assigned_at).toLocaleDateString()}</td>
                    <td>
                      <span className={`status-badge ${bm.is_active ? 'badge-active' : 'badge-inactive'}`}>
                        {bm.is_active ? 'Active' : 'Inactive'}
                      </span>
                    </td>
                    <td>
                      {bm.is_active && (
                        <button
                          className="btn-remove-small"
                          onClick={() => handleRemoveBoardMod(bm.board_moderator_id)}
                        >
                          Remove
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* Queue Items */}
      {loading ? (
        <div className="loading-state">Loading moderation queue...</div>
      ) : items.length === 0 ? (
        <div className="empty-state">
          <div className="empty-icon">✓</div>
          <h3>Queue is clear</h3>
          <p>No items match your current filters.</p>
        </div>
      ) : (
        <>
          <div className="queue-list">
            {items.map(item => (
              <div
                key={item.moderation_id}
                className={`queue-item ${selectedItem?.moderation_id === item.moderation_id ? 'selected' : ''}`}
                onClick={() => {
                  setSelectedItem(item);
                  setReviewAction('');
                  setReviewNotes('');
                }}
              >
                <div className="queue-item-preview">
                  {item.content_type === 'discussion_message' ? (
                    <div className="queue-text-preview">
                      {item.flagged_text
                        ? (item.flagged_text.length > 120 ? item.flagged_text.slice(0, 120) + '…' : item.flagged_text)
                        : '(text unavailable)'}
                    </div>
                  ) : (
                    <AuthImage
                      src={`${API_URL}/moderation/queue/${item.moderation_id}/image`}
                      alt="Flagged content"
                      className="queue-thumbnail"
                      token={token}
                    />
                  )}
                </div>
                <div className="queue-item-info">
                  <div className="queue-item-type">
                    {CONTENT_TYPE_LABELS[item.content_type] || item.content_type}
                  </div>
                  <div className="queue-item-user">
                    Uploaded by: {item.uploader_username || `User #${item.user_id}`}
                  </div>
                  <div className="queue-item-date">
                    {new Date(item.created_at).toLocaleString()}
                  </div>
                  {item.ai_flagged && (
                    <div className="queue-item-ai">
                      AI: {Math.round(item.ai_max_confidence)}% confidence
                    </div>
                  )}
                  {item.report_reason && (
                    <div className="queue-item-report">
                      Report: {item.report_reason}
                    </div>
                  )}
                </div>
                <div className="queue-item-status">
                  <span
                    className="status-badge"
                    style={{ backgroundColor: STATUS_COLORS[item.status] || '#6b7280' }}
                  >
                    {item.status}
                  </span>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className="pagination">
              <button
                disabled={page <= 1}
                onClick={() => setPage(p => p - 1)}
                className="page-btn"
              >
                ← Prev
              </button>
              <span className="page-info">
                Page {page} of {totalPages} ({total} items)
              </span>
              <button
                disabled={page >= totalPages}
                onClick={() => setPage(p => p + 1)}
                className="page-btn"
              >
                Next →
              </button>
            </div>
          )}
        </>
      )}

      {/* Review Panel */}
      {selectedItem && (
        <div className="review-panel-overlay" onClick={() => setSelectedItem(null)}>
          <div className="review-panel" onClick={(e) => e.stopPropagation()}>
            <button className="review-close" onClick={() => setSelectedItem(null)}>✕</button>

            <h2>Review Content</h2>

            <div className="review-image-container">
              {selectedItem.content_type === 'discussion_message' ? (
                <div className="review-text-content">
                  <h4 style={{ marginBottom: '8px', color: '#374151' }}>Flagged Message:</h4>
                  <div style={{
                    background: '#f9fafb',
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    padding: '16px',
                    whiteSpace: 'pre-wrap',
                    fontSize: '14px',
                    lineHeight: '1.6',
                    maxHeight: '300px',
                    overflowY: 'auto',
                    color: '#1f2937'
                  }}>
                    {selectedItem.flagged_text || '(message text unavailable — may have been deleted)'}
                  </div>
                </div>
              ) : (
                <AuthImage
                  src={`${API_URL}/moderation/queue/${selectedItem.moderation_id}/image`}
                  alt="Content under review"
                  className="review-image"
                  token={token}
                />
              )}
            </div>

            <div className="review-details">
              <div className="detail-row">
                <span className="detail-label">Type:</span>
                <span>{CONTENT_TYPE_LABELS[selectedItem.content_type] || selectedItem.content_type}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Uploader:</span>
                <span>{selectedItem.uploader_username || `User #${selectedItem.user_id}`}</span>
              </div>
              <div className="detail-row">
                <span className="detail-label">Status:</span>
                <span className="status-badge" style={{ backgroundColor: STATUS_COLORS[selectedItem.status] }}>
                  {selectedItem.status}
                </span>
              </div>
              {selectedItem.ai_flagged && (
                <>
                  <div className="detail-row">
                    <span className="detail-label">AI Confidence:</span>
                    <span>{Math.round(selectedItem.ai_max_confidence)}%</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">AI Categories:</span>
                    <div className="ai-categories">
                      {(selectedItem.ai_categories || []).map((cat, i) => (
                        <span key={i} className="ai-category-tag">
                          {cat.Name} ({Math.round(cat.Confidence || cat.Score || 0)}%)
                        </span>
                      ))}
                    </div>
                  </div>
                </>
              )}
              {selectedItem.report_reason && (
                <div className="detail-row">
                  <span className="detail-label">Report Reason:</span>
                  <span>{selectedItem.report_reason}</span>
                </div>
              )}
              {selectedItem.reviewer_username && (
                <div className="detail-row">
                  <span className="detail-label">Reviewed By:</span>
                  <span>{selectedItem.reviewer_username}</span>
                </div>
              )}
              {selectedItem.review_notes && (
                <div className="detail-row">
                  <span className="detail-label">Review Notes:</span>
                  <span>{selectedItem.review_notes}</span>
                </div>
              )}
            </div>

            {selectedItem.status === 'pending' && (
              <div className="review-actions">
                <div className="action-buttons">
                  <button
                    className={`action-btn btn-approve ${reviewAction === 'approved' ? 'active' : ''}`}
                    onClick={() => setReviewAction('approved')}
                  >
                    ✓ Approve
                  </button>
                  <button
                    className={`action-btn btn-reject ${reviewAction === 'rejected' ? 'active' : ''}`}
                    onClick={() => setReviewAction('rejected')}
                  >
                    ✕ Reject
                  </button>
                  <button
                    className={`action-btn btn-dismiss ${reviewAction === 'dismissed' ? 'active' : ''}`}
                    onClick={() => setReviewAction('dismissed')}
                  >
                    — Dismiss
                  </button>
                </div>

                <textarea
                  className="review-notes-input"
                  placeholder="Add review notes (optional)..."
                  value={reviewNotes}
                  onChange={(e) => setReviewNotes(e.target.value)}
                  rows={3}
                />

                <button
                  className="btn-submit-review"
                  disabled={!reviewAction || reviewing}
                  onClick={handleReview}
                >
                  {reviewing ? 'Submitting...' : 'Submit Review'}
                </button>
              </div>
            )}
          </div>
        </div>
      )}
        </>
      )}

      {/* ==================== WIKI EDITS TAB ==================== */}
      {activeTab === 'wiki' && (
        <div className="wiki-edits-tab">
          {wikiLoading ? (
            <div className="loading-state">Loading pending wiki edits...</div>
          ) : wikiRevisions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">✓</div>
              <h3>No pending wiki edits</h3>
              <p>All wiki contributions have been reviewed.</p>
            </div>
          ) : (
            <>
              <div className="wiki-queue-list">
                {wikiRevisions.map(rev => (
                  <div
                    key={rev.revision_id}
                    className={`wiki-queue-item ${selectedWikiRevision?.revision_id === rev.revision_id ? 'selected' : ''}`}
                    onClick={() => {
                      setSelectedWikiRevision(rev);
                      setWikiReviewNote('');
                      setWikiEditorContent('');
                      setWikiContentModified(false);
                    }}
                  >
                    <div className="wiki-queue-icon">✏️</div>
                    <div className="wiki-queue-info">
                      <div className="wiki-queue-amp">
                        {rev.manufacturer_name} {rev.amp_name} {rev.amp_model || ''}
                      </div>
                      <div className="wiki-queue-summary">
                        {rev.edit_summary || '(no summary)'}
                      </div>
                      <div className="wiki-queue-meta">
                        by <strong>{rev.author_display_name}</strong> · {new Date(rev.created_at).toLocaleString()}
                      </div>
                    </div>
                    <div className="wiki-queue-section">
                      <span className="status-badge" style={{ backgroundColor: '#6366f1' }}>
                        {WIKI_SECTION_LABELS[rev.section_type] || rev.section_type}
                      </span>
                    </div>
                  </div>
                ))}
              </div>

              {/* Wiki pagination */}
              {(() => {
                const wikiTotalPages = Math.ceil(wikiTotal / 20);
                return wikiTotalPages > 1 ? (
                  <div className="pagination">
                    <button
                      disabled={wikiPage <= 1}
                      onClick={() => setWikiPage(p => p - 1)}
                      className="page-btn"
                    >
                      ← Prev
                    </button>
                    <span className="page-info">
                      Page {wikiPage} of {wikiTotalPages} ({wikiTotal} edits)
                    </span>
                    <button
                      disabled={wikiPage >= wikiTotalPages}
                      onClick={() => setWikiPage(p => p + 1)}
                      className="page-btn"
                    >
                      Next →
                    </button>
                  </div>
                ) : null;
              })()}
            </>
          )}

          {/* Wiki Review Panel */}
          {selectedWikiRevision && (
            <div className="review-panel-overlay" onClick={() => setSelectedWikiRevision(null)}>
              <div className="review-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '800px' }}>
                <button className="review-close" onClick={() => setSelectedWikiRevision(null)}>✕</button>

                <h2>Review Wiki Edit</h2>

                <div className="review-details" style={{ marginBottom: '1rem' }}>
                  <div className="detail-row">
                    <span className="detail-label">Amplifier:</span>
                    <span style={{ fontWeight: '600' }}>
                      {selectedWikiRevision.manufacturer_name} {selectedWikiRevision.amp_name} {selectedWikiRevision.amp_model || ''}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Section:</span>
                    <span>{WIKI_SECTION_LABELS[selectedWikiRevision.section_type] || selectedWikiRevision.section_type}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Contributor:</span>
                    <span>{selectedWikiRevision.author_display_name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Summary:</span>
                    <span>{selectedWikiRevision.edit_summary || '(none)'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Submitted:</span>
                    <span>{new Date(selectedWikiRevision.created_at).toLocaleString()}</span>
                  </div>
                </div>

                {/* Editable Track Changes view — edit directly in the formatted diff */}
                <TrackedChangesView
                  oldContent={selectedWikiRevision.previous_content || ''}
                  newContent={selectedWikiRevision.content || ''}
                  editable
                  onChange={(cleanHtml) => {
                    setWikiEditorContent(cleanHtml);
                    setWikiContentModified(true);
                  }}
                  style={{ marginBottom: '0.5rem' }}
                />
                {wikiContentModified && (
                  <div style={{ fontSize: '0.8rem', color: '#059669', marginBottom: '0.5rem', fontWeight: '600' }}>
                    ✓ You've made edits — click "Request Changes" to send your suggested version to the contributor.
                  </div>
                )}

                <div className="review-actions">
                  <textarea
                    className="review-notes-input"
                    placeholder="Review note (required for Request Changes, optional for Approve/Reject)..."
                    value={wikiReviewNote}
                    onChange={(e) => setWikiReviewNote(e.target.value)}
                    rows={2}
                  />

                  <div className="action-buttons">
                    <button
                      className="action-btn btn-approve"
                      disabled={wikiReviewing}
                      onClick={() => handleWikiReview(selectedWikiRevision.revision_id, 'approve')}
                    >
                      ✓ Approve & Apply
                    </button>
                    <button
                      className="action-btn btn-request-changes"
                      disabled={wikiReviewing}
                      onClick={() => handleWikiReview(selectedWikiRevision.revision_id, 'request_changes')}
                    >
                      🔄 Request Changes
                    </button>
                    <button
                      className="action-btn btn-reject"
                      disabled={wikiReviewing}
                      onClick={() => handleWikiReview(selectedWikiRevision.revision_id, 'reject')}
                    >
                      ✕ Reject
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}

      {/* ==================== PARTS LIST TAB ==================== */}
      {activeTab === 'parts' && (
        <div className="wiki-edits-tab">
          {partsLoading ? (
            <div className="loading-state">Loading pending parts submissions...</div>
          ) : partsSubmissions.length === 0 ? (
            <div className="empty-state">
              <div className="empty-icon">✓</div>
              <h3>No pending parts submissions</h3>
              <p>All parts list contributions have been reviewed.</p>
            </div>
          ) : (
            <div className="wiki-queue-list">
              {partsSubmissions.map(sub => (
                <div
                  key={sub.submission_id}
                  className={`wiki-queue-item ${selectedPartsSubmission?.submission_id === sub.submission_id ? 'selected' : ''}`}
                  onClick={() => {
                    setSelectedPartsSubmission(sub);
                    setPartsSubmissionItems(sub.items || []);
                    setPartsReviewNote('');
                    setEditingPartItem(null);
                    setAddingPartItem(false);
                  }}
                >
                  <div className="wiki-queue-icon">🔧</div>
                  <div className="wiki-queue-info">
                    <div className="wiki-queue-amp">
                      {sub.manufacturer_name} {sub.amp_name}
                    </div>
                    <div className="wiki-queue-summary">
                      {sub.edit_summary || '(no summary)'}
                    </div>
                    <div className="wiki-queue-meta">
                      by <strong>{sub.proposer_name}</strong> · {new Date(sub.created_at).toLocaleString()}
                    </div>
                  </div>
                  <div className="wiki-queue-section">
                    <span className="status-badge" style={{ backgroundColor: '#f59e0b' }}>
                      {sub.item_count} item{Number(sub.item_count) !== 1 ? 's' : ''}
                    </span>
                  </div>
                </div>
              ))}
            </div>
          )}

          {/* Parts Review Panel */}
          {selectedPartsSubmission && (
            <div className="review-panel-overlay" onClick={() => { setSelectedPartsSubmission(null); setPartsSubmissionItems([]); }}>
              <div className="review-panel" onClick={(e) => e.stopPropagation()} style={{ maxWidth: '900px' }}>
                <button className="review-close" onClick={() => { setSelectedPartsSubmission(null); setPartsSubmissionItems([]); }}>✕</button>

                <h2>Review Parts Submission</h2>

                <div className="review-details" style={{ marginBottom: '1rem' }}>
                  <div className="detail-row">
                    <span className="detail-label">Amplifier:</span>
                    <span style={{ fontWeight: '600' }}>
                      {selectedPartsSubmission.manufacturer_name} {selectedPartsSubmission.amp_name}
                    </span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Contributor:</span>
                    <span>{selectedPartsSubmission.proposer_name}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Summary:</span>
                    <span>{selectedPartsSubmission.edit_summary || '(none)'}</span>
                  </div>
                  <div className="detail-row">
                    <span className="detail-label">Submitted:</span>
                    <span>{new Date(selectedPartsSubmission.created_at).toLocaleString()}</span>
                  </div>
                </div>

                {/* Submission Items Table */}
                <div style={{ marginBottom: '1rem' }}>
                  <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '0.5rem' }}>
                    <h3 style={{ margin: 0, fontSize: '1rem', color: '#374151' }}>Proposed Parts ({partsSubmissionItems.length})</h3>
                    <button
                      className="action-btn btn-approve"
                      style={{ fontSize: '0.8rem', padding: '0.3rem 0.8rem' }}
                      onClick={() => {
                        setAddingPartItem(true);
                        setNewPartItem({ section: '', part_name: '', value_spec: '', quantity: 1, notes: '' });
                      }}
                    >
                      + Add Item
                    </button>
                  </div>

                  <div style={{
                    border: '1px solid #e5e7eb',
                    borderRadius: '8px',
                    overflow: 'hidden',
                    maxHeight: '400px',
                    overflowY: 'auto'
                  }}>
                    <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '0.85rem' }}>
                      <thead>
                        <tr style={{ background: '#f9fafb', borderBottom: '2px solid #e5e7eb' }}>
                          <th style={{ padding: '0.5rem', textAlign: 'left' }}>Type</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left' }}>Section</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left' }}>Part Name</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left' }}>Value/Spec</th>
                          <th style={{ padding: '0.5rem', textAlign: 'center' }}>Qty</th>
                          <th style={{ padding: '0.5rem', textAlign: 'left' }}>Notes</th>
                          <th style={{ padding: '0.5rem', textAlign: 'center' }}>Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {partsSubmissionItems.map(item => (
                          <tr key={item.item_id} style={{ borderBottom: '1px solid #f3f4f6' }}>
                            {editingPartItem === item.item_id ? (
                              <>
                                <td style={{ padding: '0.4rem' }}>
                                  <span style={{
                                    fontSize: '0.7rem',
                                    padding: '0.15rem 0.4rem',
                                    borderRadius: '4px',
                                    background: item.edit_type === 'add' ? '#d1fae5' : item.edit_type === 'edit' ? '#dbeafe' : '#fee2e2',
                                    color: item.edit_type === 'add' ? '#065f46' : item.edit_type === 'edit' ? '#1e40af' : '#991b1b'
                                  }}>
                                    {item.edit_type}
                                  </span>
                                </td>
                                <td style={{ padding: '0.4rem' }}>
                                  <input
                                    type="text"
                                    value={editingPartValues.section ?? ''}
                                    onChange={(e) => setEditingPartValues(v => ({ ...v, section: e.target.value }))}
                                    style={{ width: '100%', padding: '0.25rem', fontSize: '0.8rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                  />
                                </td>
                                <td style={{ padding: '0.4rem' }}>
                                  <input
                                    type="text"
                                    value={editingPartValues.part_name ?? ''}
                                    onChange={(e) => setEditingPartValues(v => ({ ...v, part_name: e.target.value }))}
                                    style={{ width: '100%', padding: '0.25rem', fontSize: '0.8rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                  />
                                </td>
                                <td style={{ padding: '0.4rem' }}>
                                  <input
                                    type="text"
                                    value={editingPartValues.value_spec ?? ''}
                                    onChange={(e) => setEditingPartValues(v => ({ ...v, value_spec: e.target.value }))}
                                    style={{ width: '100%', padding: '0.25rem', fontSize: '0.8rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                  />
                                </td>
                                <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                                  <input
                                    type="number"
                                    min="1"
                                    value={editingPartValues.quantity ?? 1}
                                    onChange={(e) => setEditingPartValues(v => ({ ...v, quantity: parseInt(e.target.value) || 1 }))}
                                    style={{ width: '50px', padding: '0.25rem', fontSize: '0.8rem', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'center' }}
                                  />
                                </td>
                                <td style={{ padding: '0.4rem' }}>
                                  <input
                                    type="text"
                                    value={editingPartValues.notes ?? ''}
                                    onChange={(e) => setEditingPartValues(v => ({ ...v, notes: e.target.value }))}
                                    style={{ width: '100%', padding: '0.25rem', fontSize: '0.8rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                                  />
                                </td>
                                <td style={{ padding: '0.4rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                  <button
                                    onClick={() => handleSavePartItemEdit(selectedPartsSubmission.submission_id, item.item_id)}
                                    style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: '1rem', color: '#10b981' }}
                                    title="Save"
                                  >💾</button>
                                  <button
                                    onClick={() => { setEditingPartItem(null); setEditingPartValues({}); }}
                                    style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: '1rem', color: '#6b7280' }}
                                    title="Cancel"
                                  >✕</button>
                                </td>
                              </>
                            ) : (
                              <>
                                <td style={{ padding: '0.4rem' }}>
                                  <span style={{
                                    fontSize: '0.7rem',
                                    padding: '0.15rem 0.4rem',
                                    borderRadius: '4px',
                                    background: item.edit_type === 'add' ? '#d1fae5' : item.edit_type === 'edit' ? '#dbeafe' : '#fee2e2',
                                    color: item.edit_type === 'add' ? '#065f46' : item.edit_type === 'edit' ? '#1e40af' : '#991b1b'
                                  }}>
                                    {item.edit_type}
                                  </span>
                                </td>
                                <td style={{ padding: '0.4rem', fontWeight: '600', color: '#4b5563' }}>{item.section}</td>
                                <td style={{ padding: '0.4rem' }}>{item.part_name}</td>
                                <td style={{ padding: '0.4rem', color: '#6b7280' }}>{item.value_spec || '—'}</td>
                                <td style={{ padding: '0.4rem', textAlign: 'center' }}>{item.quantity || 1}</td>
                                <td style={{ padding: '0.4rem', color: '#6b7280', fontSize: '0.8rem' }}>{item.notes || '—'}</td>
                                <td style={{ padding: '0.4rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                                  <button
                                    onClick={() => {
                                      setEditingPartItem(item.item_id);
                                      setEditingPartValues({
                                        section: item.section || '',
                                        part_name: item.part_name || '',
                                        value_spec: item.value_spec || '',
                                        quantity: item.quantity || 1,
                                        notes: item.notes || ''
                                      });
                                    }}
                                    style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: '0.9rem', color: '#3b82f6' }}
                                    title="Edit"
                                  >✏️</button>
                                  <button
                                    onClick={() => handleDeletePartItem(selectedPartsSubmission.submission_id, item.item_id)}
                                    style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: '0.9rem', color: '#ef4444' }}
                                    title="Remove"
                                  >🗑️</button>
                                </td>
                              </>
                            )}
                          </tr>
                        ))}

                        {/* Add new item row */}
                        {addingPartItem && (
                          <tr style={{ borderBottom: '1px solid #f3f4f6', background: '#f0fdf4' }}>
                            <td style={{ padding: '0.4rem' }}>
                              <span style={{ fontSize: '0.7rem', padding: '0.15rem 0.4rem', borderRadius: '4px', background: '#d1fae5', color: '#065f46' }}>add</span>
                            </td>
                            <td style={{ padding: '0.4rem' }}>
                              <input
                                type="text"
                                placeholder="Section"
                                value={newPartItem.section}
                                onChange={(e) => setNewPartItem(v => ({ ...v, section: e.target.value }))}
                                style={{ width: '100%', padding: '0.25rem', fontSize: '0.8rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                              />
                            </td>
                            <td style={{ padding: '0.4rem' }}>
                              <input
                                type="text"
                                placeholder="Part name"
                                value={newPartItem.part_name}
                                onChange={(e) => setNewPartItem(v => ({ ...v, part_name: e.target.value }))}
                                style={{ width: '100%', padding: '0.25rem', fontSize: '0.8rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                              />
                            </td>
                            <td style={{ padding: '0.4rem' }}>
                              <input
                                type="text"
                                placeholder="Value/Spec"
                                value={newPartItem.value_spec}
                                onChange={(e) => setNewPartItem(v => ({ ...v, value_spec: e.target.value }))}
                                style={{ width: '100%', padding: '0.25rem', fontSize: '0.8rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                              />
                            </td>
                            <td style={{ padding: '0.4rem', textAlign: 'center' }}>
                              <input
                                type="number"
                                min="1"
                                value={newPartItem.quantity}
                                onChange={(e) => setNewPartItem(v => ({ ...v, quantity: parseInt(e.target.value) || 1 }))}
                                style={{ width: '50px', padding: '0.25rem', fontSize: '0.8rem', border: '1px solid #d1d5db', borderRadius: '4px', textAlign: 'center' }}
                              />
                            </td>
                            <td style={{ padding: '0.4rem' }}>
                              <input
                                type="text"
                                placeholder="Notes"
                                value={newPartItem.notes}
                                onChange={(e) => setNewPartItem(v => ({ ...v, notes: e.target.value }))}
                                style={{ width: '100%', padding: '0.25rem', fontSize: '0.8rem', border: '1px solid #d1d5db', borderRadius: '4px' }}
                              />
                            </td>
                            <td style={{ padding: '0.4rem', textAlign: 'center', whiteSpace: 'nowrap' }}>
                              <button
                                onClick={() => handleAddPartItem(selectedPartsSubmission.submission_id)}
                                style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: '1rem', color: '#10b981' }}
                                title="Save"
                              >💾</button>
                              <button
                                onClick={() => setAddingPartItem(false)}
                                style={{ cursor: 'pointer', background: 'none', border: 'none', fontSize: '1rem', color: '#6b7280' }}
                                title="Cancel"
                              >✕</button>
                            </td>
                          </tr>
                        )}
                      </tbody>
                    </table>
                  </div>
                </div>

                {/* Review actions */}
                <div className="review-actions">
                  <textarea
                    className="review-notes-input"
                    placeholder="Review note (required for Reject, optional for Approve)..."
                    value={partsReviewNote}
                    onChange={(e) => setPartsReviewNote(e.target.value)}
                    rows={2}
                  />

                  <div className="action-buttons">
                    <button
                      className="action-btn btn-approve"
                      disabled={partsReviewing}
                      onClick={() => handlePartsReview(selectedPartsSubmission.submission_id, 'approve')}
                    >
                      ✓ Approve & Apply
                    </button>
                    <button
                      className="action-btn btn-reject"
                      disabled={partsReviewing}
                      onClick={() => handlePartsReview(selectedPartsSubmission.submission_id, 'reject')}
                    >
                      ✕ Reject
                    </button>
                  </div>
                </div>
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ModerationScreen;
